import React, { useEffect, useState } from "react";

/**
 * Architectural line-drawing "complex" behind the mortgage lander's hero.
 * A composed elevation of five buildings sharing a ground line and site-plan
 * dimension marks, read left → right: a prairie-style long house with car
 * port, a run of three attached rowhouses, a three-storey modernist cube
 * apartment block, a six-storey mid-rise (sits directly behind the form
 * card), and a traditional gabled house with dormer, porch, muntined
 * windows and a side garage.
 *
 * Two stroke weights (0.7 outline / 0.4 interior detail) plus a middleweight
 * (0.55 for window subdivisions + door panels) approximate architectural
 * ink. Trees and dashed dimension lines carry the site-plan feel without
 * turning the backdrop into noise.
 *
 * Desktop only. Auto-disabled on:
 *   - viewport < 1024px
 *   - navigator.connection.saveData / slow-2g / 2g
 * Wrapper opacity breathe + slow vertical drift are transform/opacity-only
 * and auto-freeze under the global `@media (prefers-reduced-motion: reduce)`
 * block in index.css.
 */
export default function HeroBackdrop() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const desktop = window.matchMedia("(min-width: 1024px)").matches;
    const saveData =
      typeof navigator !== "undefined" &&
      navigator.connection &&
      (navigator.connection.saveData ||
        ["slow-2g", "2g"].includes(navigator.connection.effectiveType));
    setShow(desktop && !saveData);
  }, []);

  if (!show) return null;

  // Common stroke props - cuts inline noise below.
  const s = (w = 0.7) => ({
    fill: "none",
    stroke: "currentColor",
    strokeWidth: w,
    vectorEffect: "non-scaling-stroke",
  });

  return (
    <div className="hero-backdrop" aria-hidden="true">
      <div className="hb-complex">
        <svg
          viewBox="0 0 1600 500"
          preserveAspectRatio="xMidYEnd meet"
          width="100%"
          height="100%"
        >
          {/* ─── 1. PRAIRIE-STYLE LONG HOUSE (far left, extends off-canvas) ─── */}
          <g>
            {/* Cantilevered flat roof - extends past both walls */}
            <line x1="-20" y1="360" x2="330" y2="360" {...s(0.7)} />
            <line x1="-20" y1="365" x2="330" y2="365" {...s(0.4)} />
            {/* Body */}
            <rect x="20" y="360" width="270" height="90" {...s(0.7)} />
            {/* Prairie horizontal belt line */}
            <line x1="20" y1="390" x2="290" y2="390" {...s(0.4)} />
            {/* Ribbon window: 5 panes */}
            {[0, 1, 2, 3, 4].map((i) => (
              <rect key={i} x={38 + i * 42} y="400" width="34" height="34" {...s(0.4)} />
            ))}
            {/* Chimney offset toward the right */}
            <rect x="240" y="330" width="16" height="30" {...s(0.7)} />
            {/* Attached carport wing on the right end */}
            <line x1="290" y1="380" x2="360" y2="380" {...s(0.7)} />
            <line x1="290" y1="450" x2="360" y2="450" {...s(0.55)} />
            <line x1="360" y1="380" x2="360" y2="450" {...s(0.55)} />
          </g>

          {/* ─── Small tree between prairie and rowhouse ─── */}
          <g>
            <line x1="395" y1="440" x2="395" y2="465" {...s(0.55)} />
            <circle cx="395" cy="428" r="16" {...s(0.55)} />
            <circle cx="395" cy="428" r="10" {...s(0.4)} />
          </g>

          {/* ─── 2. ROWHOUSE TERRACE - 3 attached two-storey units ─── */}
          <g>
            {/* Shared roof line + parapet */}
            <line x1="430" y1="290" x2="700" y2="290" {...s(0.7)} />
            <line x1="430" y1="295" x2="700" y2="295" {...s(0.4)} />
            {/* Full terrace body */}
            <rect x="430" y="295" width="270" height="160" {...s(0.7)} />
            {/* Party walls (dashed vertical dividers) */}
            <line x1="520" y1="295" x2="520" y2="455" {...s(0.4)} strokeDasharray="3 3" />
            <line x1="610" y1="295" x2="610" y2="455" {...s(0.4)} strokeDasharray="3 3" />
            {/* Floor line between the two storeys */}
            <line x1="430" y1="378" x2="700" y2="378" {...s(0.4)} />
            {/* Upper-storey windows - one per unit, tall */}
            <rect x="454" y="310" width="42" height="52" {...s(0.55)} />
            <line x1="475" y1="310" x2="475" y2="362" {...s(0.35)} />
            <rect x="544" y="310" width="42" height="52" {...s(0.55)} />
            <line x1="565" y1="310" x2="565" y2="362" {...s(0.35)} />
            <rect x="634" y="310" width="42" height="52" {...s(0.55)} />
            <line x1="655" y1="310" x2="655" y2="362" {...s(0.35)} />
            {/* Ground floor - door + stoop per unit */}
            <rect x="466" y="405" width="18" height="50" {...s(0.55)} />
            <rect x="556" y="405" width="18" height="50" {...s(0.55)} />
            <rect x="646" y="405" width="18" height="50" {...s(0.55)} />
            <line x1="463" y1="455" x2="487" y2="455" {...s(0.4)} />
            <line x1="553" y1="455" x2="577" y2="455" {...s(0.4)} />
            <line x1="643" y1="455" x2="667" y2="455" {...s(0.4)} />
            {/* Window next to each door */}
            <rect x="493" y="400" width="24" height="30" {...s(0.4)} />
            <rect x="583" y="400" width="24" height="30" {...s(0.4)} />
            <rect x="673" y="400" width="24" height="30" {...s(0.4)} />
          </g>

          {/* ─── 3. MODERNIST CUBE APARTMENTS (3 storey, centre-back) ─── */}
          <g>
            {/* Cubic body */}
            <rect x="740" y="220" width="240" height="235" {...s(0.7)} />
            {/* Flat roof cap */}
            <line x1="734" y1="220" x2="986" y2="220" {...s(0.7)} />
            {/* Penthouse setback */}
            <rect x="820" y="195" width="90" height="25" {...s(0.7)} />
            {/* Side vertical fin */}
            <line x1="734" y1="220" x2="734" y2="455" {...s(0.4)} />
            {/* 4x3 window grid */}
            {[0, 1, 2].map((row) =>
              [0, 1, 2, 3].map((col) => (
                <rect
                  key={`c-${row}-${col}`}
                  x={758 + col * 54}
                  y={240 + row * 62}
                  width="38"
                  height="46"
                  {...s(0.4)}
                />
              ))
            )}
            {/* Entry canopy cantilever */}
            <rect x="820" y="418" width="60" height="12" {...s(0.55)} />
            {/* Ground-floor entry door */}
            <rect x="836" y="430" width="28" height="25" {...s(0.55)} />
          </g>

          {/* ─── Small tree between cube and mid-rise ─── */}
          <g>
            <line x1="1010" y1="440" x2="1010" y2="465" {...s(0.55)} />
            <circle cx="1010" cy="428" r="14" {...s(0.55)} />
          </g>

          {/* ─── 4. MID-RISE APARTMENT TOWER (6-storey, sits behind form) ─── */}
          <g>
            {/* Slim tower body */}
            <rect x="1040" y="70" width="180" height="385" {...s(0.7)} />
            {/* Parapet cap */}
            <line x1="1034" y1="70" x2="1226" y2="70" {...s(0.7)} />
            {/* Roof mechanical box */}
            <rect x="1080" y="48" width="60" height="22" {...s(0.55)} />
            <line x1="1080" y1="55" x2="1140" y2="55" {...s(0.4)} />
            {/* 6-storey window grid: 4 per floor */}
            {[0, 1, 2, 3, 4, 5].map((row) => (
              <g key={`t-${row}`}>
                {/* Spandrel line between floors */}
                <line x1="1040" y1={98 + row * 60} x2="1220" y2={98 + row * 60} {...s(0.4)} />
                {[0, 1, 2, 3].map((col) => (
                  <rect
                    key={`tw-${row}-${col}`}
                    x={1052 + col * 42}
                    y={104 + row * 60}
                    width="30"
                    height="42"
                    {...s(0.4)}
                  />
                ))}
              </g>
            ))}
            {/* Entry canopy at base */}
            <rect x="1100" y="420" width="60" height="10" {...s(0.55)} />
            <rect x="1120" y="430" width="20" height="25" {...s(0.55)} />
          </g>

          {/* ─── 5. GABLED HOUSE with dormer, porch, side garage (right) ─── */}
          <g>
            {/* Wall body */}
            <rect x="1290" y="280" width="240" height="175" {...s(0.7)} />
            {/* Steep gable roof */}
            <polygon points="1268,280 1410,175 1552,280" {...s(0.7)} />
            {/* Roof soffit inner line */}
            <polygon points="1280,280 1410,188 1540,280" {...s(0.4)} />
            {/* Chimney with cap */}
            <rect x="1480" y="205" width="20" height="45" {...s(0.7)} />
            <line x1="1476" y1="205" x2="1504" y2="205" {...s(0.7)} />
            {/* Dormer */}
            <polygon points="1332,262 1358,236 1384,262" {...s(0.4)} />
            <rect x="1340" y="262" width="36" height="18" {...s(0.4)} />
            {/* Porch roof + two posts (centered) */}
            <line x1="1362" y1="350" x2="1458" y2="350" {...s(0.7)} />
            <line x1="1362" y1="350" x2="1362" y2="455" {...s(0.4)} />
            <line x1="1458" y1="350" x2="1458" y2="455" {...s(0.4)} />
            {/* Paneled door with lintel */}
            <rect x="1390" y="365" width="40" height="90" {...s(0.55)} />
            <line x1="1390" y1="377" x2="1430" y2="377" {...s(0.4)} />
            <line x1="1410" y1="380" x2="1410" y2="455" {...s(0.4)} />
            {/* Flanking windows with muntins */}
            <rect x="1308" y="310" width="46" height="40" {...s(0.55)} />
            <line x1="1331" y1="310" x2="1331" y2="350" {...s(0.35)} />
            <line x1="1308" y1="330" x2="1354" y2="330" {...s(0.35)} />
            <rect x="1466" y="310" width="46" height="40" {...s(0.55)} />
            <line x1="1489" y1="310" x2="1489" y2="350" {...s(0.35)} />
            <line x1="1466" y1="330" x2="1512" y2="330" {...s(0.35)} />
            {/* Side garage (right of the house, extends off-canvas) */}
            <rect x="1530" y="360" width="90" height="95" {...s(0.7)} />
            <line x1="1530" y1="370" x2="1620" y2="370" {...s(0.55)} />
            <rect x="1542" y="384" width="66" height="60" {...s(0.55)} />
            {/* Garage door panels */}
            <line x1="1542" y1="399" x2="1608" y2="399" {...s(0.35)} />
            <line x1="1542" y1="414" x2="1608" y2="414" {...s(0.35)} />
            <line x1="1542" y1="429" x2="1608" y2="429" {...s(0.35)} />
          </g>

          {/* ─── Small tree far right, beyond the garage ─── */}
          <g>
            <line x1="1500" y1="445" x2="1500" y2="465" {...s(0.55)} />
            <circle cx="1500" cy="432" r="14" {...s(0.4)} />
          </g>

          {/* ─── Shared ground line + dashed site-plan dimension ─── */}
          <line x1="-20" y1="455" x2="1620" y2="455" {...s(0.7)} />
          <line x1="20" y1="475" x2="1580" y2="475" {...s(0.4)} strokeDasharray="3 5" />
          <line x1="20" y1="470" x2="20" y2="480" {...s(0.4)} />
          <line x1="1580" y1="470" x2="1580" y2="480" {...s(0.4)} />
        </svg>
      </div>
    </div>
  );
}
